import { useState, useCallback } from 'react';

export type StampType = 'heart' | 'star' | 'qrcode' | 'upload' | 'text';

export interface TextData {
    text: string;
    fontSize: number;
    color: string;
    /** 文字底色；未設定 = 透明無底色 */
    backgroundColor?: string;
}

export type ShapeKind = 'heart' | 'star' | 'rect' | 'circle';

export interface ShapeMeta {
    kind: ShapeKind;
    color: string;
    lineWidth: number;
    filled: boolean;
}

export interface Stamp {
    id: string;
    type: StampType;
    /** Position as % of image container width/height */
    xPct: number;
    yPct: number;
    /** Width as % of container width */
    sizePct: number;
    /** Height as % of container height；未設則用原圖自然比例（h-auto） */
    heightPct?: number;
    /** For qrcode and upload stamps: the image data URL */
    imageDataUrl?: string;
    /** For text stamps: 保留原始文字資料供再次編輯 */
    textData?: TextData;
    /** For shape stamps: 保留形狀資料供合成時用 canvas 直接繪製，避免 SVG 在 raster 時 stroke 失真 */
    shapeMeta?: ShapeMeta;
}

export interface AddStampOptions {
    heightPct?: number;
    textData?: TextData;
    shapeMeta?: ShapeMeta;
}

export function useStamps() {
    const [stamps, setStamps] = useState<Stamp[]>([]);

    const addStamp = useCallback((type: StampType, imageDataUrl?: string, options?: AddStampOptions) => {
        const id = `stamp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setStamps(prev => [
            ...prev,
            {
                id, type, xPct: 40, yPct: 40, sizePct: 20, imageDataUrl,
                ...(options?.heightPct !== undefined ? { heightPct: options.heightPct } : {}),
                ...(options?.textData ? { textData: options.textData } : {}),
                ...(options?.shapeMeta ? { shapeMeta: options.shapeMeta } : {}),
            },
        ]);
    }, []);

    const updateStamp = useCallback((id: string, partial: Partial<Omit<Stamp, 'id' | 'type'>>) => {
        setStamps(prev => prev.map(s => s.id === id ? { ...s, ...partial } : s));
    }, []);

    const removeStamp = useCallback((id: string) => {
        setStamps(prev => prev.filter(s => s.id !== id));
    }, []);

    const clearStamps = useCallback(() => setStamps([]), []);

    return { stamps, addStamp, updateStamp, removeStamp, clearStamps };
}
